/**
 * Air Traffic Controller (ATC) LLM Service — the classify behavior's model
 * call in the browser.
 *
 * The job, prompt, parse and the behavior's switches are the shared core (one
 * copy with the server runner): supabase/functions/_shared/classifyCore.ts.
 * The workspace prompt is an overlay on the built-in contract through the
 * instruction stack, never a replacement (SPEC-custom-agent-template.md §2 H).
 *
 * Runs through Peak LLM only — honors workspace model + model parameters.
 * Throws {@link AgentLlmError} when Peak LLM is unavailable or misconfigured.
 */

import {
  buildClassifyPrompt,
  buildClassifySystemPrompt,
  CLASSIFY_JOB,
  parseClassifyResponse,
  type ClassifyInput,
  type ClassifyResult,
  type ExecutionProfile,
} from '../../supabase/functions/_shared/classifyCore.ts';
import { resolveModelParams } from '../../supabase/functions/_shared/estimateValueCore.ts';
import { AgentLlmError, callAgentLlm } from './llmClient';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

export type { ExecutionProfile };
export type AtcResult = ClassifyResult;

export interface AtcInput extends ClassifyInput {
  /** Model to use (defaults to the shared default, GPT-6.1 Sol) */
  model?: string;
  /** The workspace's prompt overlay — it specializes the contract, never replaces it. */
  systemPrompt?: string | null;
  /** agent_settings.model_params, held inside the behavior's bound. */
  modelParams?: Record<string, unknown> | null;
  timeoutMs?: number;
}

/**
 * Run the classify behavior's model call on a single item.
 * Throws {@link AgentLlmError} on failure; never invents an answer.
 */
export async function runAtcAgent(input: AtcInput): Promise<{ result: AtcResult; model: string }> {
  if (!input.title.trim()) {
    throw new AgentLlmError('Item title is required for ATC classification.');
  }

  const model = input.model ?? DEFAULT_MODEL;
  const params = resolveModelParams(CLASSIFY_JOB.modelDefaults, input.modelParams ?? null);
  const { systemPrompt } = buildClassifySystemPrompt({ overlay: input.systemPrompt ?? null, language: params.language });

  const content = await callAgentLlm({
    userPrompt: buildClassifyPrompt(input),
    systemPrompt,
    model,
    conversationId: `atc-${Date.now()}`,
    maxTokens: params.maxTokens,
    temperature: params.temperature,
    timeoutMs: input.timeoutMs,
  });

  const result = parseClassifyResponse(content);
  if (!result) {
    throw new AgentLlmError('ATC agent could not parse the LLM response. Check Peak LLM logs.');
  }
  return { result, model };
}
